/**
 * Global error handler setup for browser environments.
 *
 * Captures uncaught errors and unhandled promise rejections, routing them
 * through the shared logger so they reach the backend via the HTTP transport.
 */

import type { Logger } from './logger';
import { reportClientError } from './error-reporting';
import { buildHydrationFingerprint, isHydrationMessage } from './hydration-errors';

/**
 * Install global error handlers that forward to the provided logger.
 * Safe to call multiple times — subsequent calls are no-ops.
 *
 * Captures:
 *   - window.onerror (uncaught JS errors)
 *   - window.onunhandledrejection (unhandled promise rejections)
 */
export function installGlobalErrorHandlers(logger: Logger): void {
  if (typeof window === 'undefined') return;

  // Guard against double-install
  if ((window as WindowWithLoggerInstalled).__colix_error_handlers_installed) return;
  (window as WindowWithLoggerInstalled).__colix_error_handlers_installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    const error = event.error instanceof Error
      ? event.error
      : new Error(event.message || 'Uncaught error');

    const isHydrationError = isHydrationMessage(error.message);

    reportClientError(logger, error, {
      component: 'global-error-handler',
      action: 'uncaught_error',
      source_file: event.filename,
      error_line: event.lineno,
      error_col: event.colno,
      // Capture the page URL (createEntry sets `url` to window.location.href;
      // previously we overwrote it with event.filename)
      page_url: window.location.href,
      // For hydration errors, the error often has extra info attached
      ...(isHydrationError
        ? {
            error_category: 'hydration',
            error_fingerprint: buildHydrationFingerprint(error.message),
          }
        : {}),
    }, {
      reporter: 'window.onerror',
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason ?? 'Unhandled promise rejection'));

    reportClientError(logger, error, {
      component: 'global-error-handler',
      action: 'unhandled_rejection',
      page_url: window.location.href,
    }, {
      reporter: 'window.onunhandledrejection',
    });
  });
}

interface WindowWithLoggerInstalled extends Window {
  __colix_error_handlers_installed?: boolean;
}
